import Student from '../../../model/Student';
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CustomTable from '../CustomTable';
import { MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {
	assignStage3JuryPairManual,
	getStage3JuryPairs,
	getThirdAStageStudents,
	regenerateStage3WorkCode,
	setStage3CompletenessAutoAccess,
	setStage3WorkCode,
	updateThirdAStageResult,
	updateThirdAWorkStatus,
} from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import Result from '../../../model/Result';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { decodeBase64Utf8 } from '../../../utils/string-tools';
import OlympicsContext from '../../../store/olympics-context';
import { filter } from '../StagesTable';
import { debugError } from '../../../utils/debug';
import ContactLinks from '../ContactLinks';
import ProtocolVerificationDialog from './ProtocolVerificationDialog';

const ThirdAStageTable = () => {
	type JuryPair = {
		id: number;
		name: string;
		juror1_district_id: number;
		juror2_district_id: number;
	};
	const WORK_STATUS_OPTIONS = ['SKAN', 'AUTO-SKAN', 'AUTOSKANOK', 'PAPIER', 'INNE'];

	const [allStudents, setAllStudents] = useState([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [juryPairs, setJuryPairs] = useState<JuryPair[]>([]);
	const [assignDialogOpen, setAssignDialogOpen] = useState(false);
	const [assignStudent, setAssignStudent] = useState<Student | null>(null);
	const [assignPairId, setAssignPairId] = useState<number | ''>('');
	const [assigning, setAssigning] = useState(false);
	const [workStatusDialogOpen, setWorkStatusDialogOpen] = useState(false);
	const [workStatusStudent, setWorkStatusStudent] = useState<Student | null>(null);
	const [workStatusValue, setWorkStatusValue] = useState<string>('');
	const [workStatusSaving, setWorkStatusSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
	const { logout, token, isAdmin, isSecretary, id } = useContext(AuthContext);
	const { districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing } = useContext(OlympicsContext);
	const isRefreshingRef = useRef(false);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const formatStage3Code = (code: string | null | undefined) =>
		code && code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code || '';

	const filterStudents = (list: Student[]) => {
		let filtered = filter(list, districtFilterId);

		if (searchFilterValue) {
			filtered = filtered.filter((s) => {
				const matchesSearch = (value: string | undefined) =>
					value && value.toLowerCase().includes(searchFilterValue.toLowerCase());
				const normalizedSearch = searchFilterValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
				const normalizedCode = (s.stage3WorkCode || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
				const matchesCode = normalizedSearch.length > 0 && normalizedCode.includes(normalizedSearch);

				return (
					matchesSearch(s.firstName) ||
					matchesSearch(s.lastName) ||
					matchesCode ||
					matchesSearch(s.email) ||
					matchesSearch(s.school?.name) ||
					matchesSearch(s.district?.name) ||
					s.teachers?.some((t: any) => matchesSearch(t.firstName) || matchesSearch(t.lastName))
				);
			});
		}

		return filtered;
	};

	useEffect(() => {
		setDisplayedStudents(filterStudents(allStudents));
	}, [districtFilterId, searchFilterValue, allStudents]);

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}
		getThirdAStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
				} else {
					debugError('ThirdAStageTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	const loadJuryPairs = () => {
		if (!isAdmin) {
			return;
		}
		getStage3JuryPairs(token)
			.then((response) => {
				if (response.status === 200) {
					const pairs = Array.isArray(response.data?.pairs) ? response.data.pairs : [];
					setJuryPairs(
						pairs.map((p: any) => ({
							id: Number(p.id),
							name: String(p.name || ''),
							juror1_district_id: Number(p.juror1_district_id || 0),
							juror2_district_id: Number(p.juror2_district_id || 0),
						})),
					);
				}
			})
			.catch(() => {
				setJuryPairs([]);
			});
	};

	const handleSave = () => {
		setIsUpdating(true);
		const data = {
			id: editedStudent?.id,
			thirdAStageTopic: editedStudent?.result.thirdAStageTopic,
			thirdAStagePoints: editedStudent?.result.thirdAStagePoints,
		};
		updateThirdAStageResult(token, data)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
					const warningEncoded = response.headers?.['x-olympics-warning-b64'];
					const warningFallback = response.headers?.['x-olympics-warning'];
					if (warningEncoded || warningFallback) {
						const warning = warningEncoded ? decodeBase64Utf8(warningEncoded) : warningFallback;
						setMsg({ msg: warning, severity: 'warning' });
					}
				} else {
					debugError('ThirdAStageTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	const handleStage3CodeDoubleClick = (student: Student) => {
		if (!isAdmin || !student?.id) {
			return;
		}

		const hasExistingCode = Boolean(student.stage3WorkCode && student.stage3WorkCode.trim() !== '');
		if (hasExistingCode) {
			const accepted = window.confirm('Czy wygenerować nowy kod pracy III dla tego ucznia?');
			if (!accepted) {
				return;
			}
		}

		regenerateStage3WorkCode(token, student.id)
			.then((response) => {
				if (response.status === 200) {
					const newCode = String(response?.data?.work_code || '');
					setAllStudents((prev: any) =>
						prev.map((s: Student) => (s.id === student.id ? { ...s, stage3WorkCode: newCode } : s)),
					);
					setMsg({ msg: 'Kod pracy III został zaktualizowany', severity: 'success' });
				} else {
					setMsg({ msg: response.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
			});
	};

	const handleStage3CodeContextMenu = (event: MouseEvent, student: Student) => {
		if (!isAdmin || !student?.id) {
			return;
		}
		event.preventDefault();
		const current = String(student.stage3WorkCode || '');
		const suggested = formatStage3Code(current);
		const input = window.prompt('Podaj kod pracy III (8 znaków, litery/cyfry):', suggested);
		if (input === null) {
			return;
		}
		const normalized = input
			.trim()
			.toUpperCase()
			.replace(/[^A-Z0-9]/g, '');
		if (normalized.length !== 8) {
			setMsg({ msg: 'Kod pracy musi mieć dokładnie 8 znaków alfanumerycznych.' });
			return;
		}
		setStage3WorkCode(token, student.id, normalized)
			.then((response) => {
				if (response.status === 200) {
					const nextCode = String(response?.data?.work_code || normalized);
					setAllStudents((prev: any) =>
						prev.map((s: Student) => (s.id === student.id ? { ...s, stage3WorkCode: nextCode } : s)),
					);
					setMsg({ msg: 'Kod pracy III został zapisany', severity: 'success' });
				} else {
					setMsg({ msg: response.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
			});
	};

	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			downloadData(false);
		} else {
			downloadData(true);
		}
		loadJuryPairs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger, token]);

	const getAllowedPairsForStudent = (student: Student): JuryPair[] =>
		juryPairs.filter(
			(pair) =>
				Number(pair.juror1_district_id) !== Number(student?.district?.id) &&
				Number(pair.juror2_district_id) !== Number(student?.district?.id),
		);

	const openAssignDialog = (student: Student) => {
		const currentPairId = Number(student?.result?.thirdAJuryPairId || 0);
		setAssignStudent(student);
		setAssignPairId(currentPairId > 0 ? currentPairId : '');
		setAssignDialogOpen(true);
	};

	const handleAssignSave = () => {
		if (!assignStudent?.id) {
			return;
		}
		setAssigning(true);
		assignStage3JuryPairManual(token, {
			student_id: Number(assignStudent.id),
			jury_pair_id: assignPairId === '' ? null : Number(assignPairId),
		})
			.then((response) => {
				if (response.status === 200) {
					setMsg({ msg: 'Zapisano przypisanie komisji.', severity: 'success' });
					setAssignDialogOpen(false);
					setAssignStudent(null);
					setAssignPairId('');
					downloadData(false);
					loadJuryPairs();
				} else {
					setMsg({ msg: response.data?.message || 'Nie udało się zapisać przypisania.' });
				}
			})
			.catch((exception) => {
				setMsg({ msg: exception?.response?.data?.message || 'Błąd przypisania komisji.' });
			})
			.finally(() => {
				setAssigning(false);
			});
	};

	const openWorkStatusDialog = (student: Student) => {
		setWorkStatusStudent(student);
		setWorkStatusValue(student?.result?.thirdAWorkStatus || '');
		setWorkStatusDialogOpen(true);
	};

	const handleWorkStatusSave = () => {
		if (!workStatusStudent?.id) {
			return;
		}
		setWorkStatusSaving(true);
		updateThirdAWorkStatus(token, {
			student_id: Number(workStatusStudent.id),
			status: workStatusValue || null,
		})
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setWorkStatusDialogOpen(false);
					setWorkStatusStudent(null);
					setWorkStatusValue('');
					setMsg({ msg: 'Zaktualizowano status pracy.', severity: 'success' });
				} else {
					setMsg({ msg: response.data?.message || 'Nie udało się zapisać statusu pracy.' });
				}
			})
			.catch((exception) => {
				setMsg({ msg: exception?.response?.data?.message || 'Błąd aktualizacji statusu pracy.' });
			})
			.finally(() => {
				setWorkStatusSaving(false);
			});
	};

	const handlePointsChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = Number(event.target.value);
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const handleTextChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = event?.target?.value ? event.target.value.toUpperCase() : '';
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const renderPointsField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				type={'number'}
				value={editedStudent.result[accessor]}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handlePointsChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const renderEditableField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				value={editedStudent.result[accessor]}
				inputProps={{ maxLength: 2 }}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handleTextChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const data = (input: any) => {
		const contactColumns = isSecretary ? [{ header: 'Kontakt', accessor: 'contact', align: 'left' }] : [];
		let additionalColumns: any[] = [];

		if (isAdmin || isSecretary) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}

		const codeColumn = isAdmin
			? [{ header: 'Kod pracy III', accessor: 'stage3Code', align: 'center', width: '140px' }]
			: [];
		const juryPairColumn = isAdmin ? [{ header: 'Komisja', accessor: 'juryPair', align: 'left', width: '210px' }] : [];
		const workStatusColumn = isAdmin
			? [{ header: 'Status pracy', accessor: 'workStatus', align: 'left', width: '220px' }]
			: [];
		const driveCopiedColumn = isAdmin
			? [{ header: 'Skopiowane jurorom', accessor: 'driveCopied', align: 'left', width: '190px' }]
			: [];
		const specialCertificateColumn =
			isAdmin || isSecretary
				? [{ header: 'Orzeczenie', accessor: 'specialCertificate', align: 'center', width: '120px' }]
				: [];

		return {
			columns: [
				{ header: 'Lp.', accessor: 'index', align: 'left', width: '70px' },
				{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
				...codeColumn,
				...juryPairColumn,
				...workStatusColumn,
				...driveCopiedColumn,
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				...additionalColumns,
				...contactColumns,
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left', width: '140px' },
				{ header: 'Nauczyciel', accessor: 'teachers', align: 'left', width: '160px' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				...specialCertificateColumn,
				{ header: 'Specjalizacja', accessor: 'specialization', width: '150px', align: 'center' },
				{ header: 'Temat części ustnej', accessor: 'oralTopic', width: '220px', align: 'left' },
				{ header: 'Temat z językoznawstwa', accessor: 'linguisticTopic', width: '220px', align: 'left' },
				{
					header: 'Auto udost.',
					accessor: 'autoAccess',
					width: '160px',
					align: 'center',
					editable: isAdmin,
				},
				{ header: 'Temat części pisemnej', accessor: 'subject', width: '220px', align: 'center', editable: isAdmin },
				{ header: 'Punkty', accessor: 'points', width: '100px', align: 'center', editable: isAdmin },
				{ header: 'Zakwalifikowany', accessor: 'qualified', width: '170px', align: 'center' },
				{ header: 'Tytuł', accessor: 'title', width: '150px', align: 'center' },
				{ header: '', accessor: 'action', align: 'center', width: '100px', nosort: true, editable: isAdmin },
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							id: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
								</Typography>
							),
							stage3Code: (
								<Typography
									variant='caption'
									fontWeight='medium'
									onDoubleClick={() => handleStage3CodeDoubleClick(i)}
									onContextMenu={(event) => handleStage3CodeContextMenu(event, i)}
									title={isAdmin ? 'Dwuklik: wygeneruj kod | Prawy klik: dodaj/edytuj kod' : ''}
									sx={{ cursor: isAdmin ? 'pointer' : 'default', display: 'inline-block', minWidth: '64px' }}
								>
									{formatStage3Code(i.stage3WorkCode) || '\u00A0'}
								</Typography>
							),
							juryPair: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.thirdAJuryPairName || '-'}
									{isAdmin && (
										<IconButton
											size='small'
											sx={{ ml: 0.5 }}
											onClick={() => openAssignDialog(i)}
											title='Edytuj komisję'
										>
											<EditIcon fontSize='inherit' />
										</IconButton>
									)}
								</Typography>
							),
							workStatus: (
								<Typography
									variant='caption'
									fontWeight='medium'
									sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
								>
									<Checkbox
										size='small'
										checked={Boolean(i.result.thirdAWorkStatus)}
										disabled
									/>
									{i.result.thirdAWorkStatus || ''}
									{isAdmin && (
										<IconButton
											size='small'
											sx={{ ml: 0.5 }}
											onClick={() => openWorkStatusDialog(i)}
											title='Edytuj status pracy'
										>
											<EditIcon fontSize='inherit' />
										</IconButton>
									)}
								</Typography>
							),
							driveCopied: (
								<Typography
									variant='caption'
									fontWeight='medium'
									sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
								>
									<Checkbox
										size='small'
										checked={Boolean(i.result.thirdADriveCopiedAt)}
										disabled
									/>
									{i.result.thirdADriveCopiedAt ? 'TAK' : 'NIE'}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							contact: isSecretary && <ContactLinks student={i} />,
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.cityWithCountry}
								</Typography>
							),
							district: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district?.name || ''}
								</Typography>
							),
							teachers: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{(i.teachers || [])
										.map((t) => `${t.firstName} ${t.lastName}`.trim())
										.filter(Boolean)
										.join(', ')}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClassText && i.schoolClassText.trim() !== '' ? i.schoolClassText : String(i.schoolClass)}
								</Typography>
							),
							specialCertificate: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialCertificate ? 'TAK' : 'NIE'}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							oralTopic: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.topic.topic}
								</Typography>
							),
							linguisticTopic: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.topic.linguisticTopic}
								</Typography>
							),
							autoAccess: isAdmin ? (
								<Button
									size='small'
									variant={i.result.thirdAAutoAccessDisabled ? 'contained' : 'outlined'}
									color={i.result.thirdAAutoAccessDisabled ? 'warning' : 'inherit'}
									onClick={() => {
										setStage3CompletenessAutoAccess(token, i.id, !i.result.thirdAAutoAccessDisabled)
											.then(() => {
												setAllStudents((prev: any) =>
													prev.map((s: Student) =>
														s.id === i.id
															? {
																	...s,
																	result: {
																		...s.result,
																		thirdAAutoAccessDisabled: !i.result.thirdAAutoAccessDisabled,
																	},
																}
															: s,
													),
												);
												setMsg({
													msg: `Wycofanie automatycznej kwalifikacji: ${
														!i.result.thirdAAutoAccessDisabled ? 'TAK' : 'NIE'
													}`,
													severity: 'success',
												});
											})
											.catch((exception) => {
												const msg = exception?.response?.data?.message
													? exception?.response?.data?.message
													: DEFAULT_API_ERROR;
												setMsg({ msg, severity: 'error' });
											});
									}}
								>
									{i.result.thirdAAutoAccessDisabled ? 'WYCOFANE' : 'AKTYWNE'}
								</Button>
							) : (
								<Typography variant='caption'>—</Typography>
							),
							subject: renderEditableField(i, 'thirdAStageTopic'),
							points: renderPointsField(i, 'thirdAStagePoints'),
							title: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.finalTitle}
								</Typography>
							),
							qualified: (
								<Checkbox
									checked={i.result.thirdAStageQualified}
									disabled={true}
								/>
							),
							action: (
								<ActionButtons
									student={i}
									disabled={!isAdmin}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSave}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => setEditedStudent(i)}
								/>
							),
						};
					})
				: [],
		};
	};
	const footerButtons = isAdmin ? (
		<Tooltip title='Weryfikacja protokołu (Etap IIIA)'>
			<IconButton
				size='small'
				sx={{ color: 'white' }}
				onClick={() => setVerificationDialogOpen(true)}
			>
				<FileUploadIcon fontSize='small' />
			</IconButton>
		</Tooltip>
	) : null;

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<CustomTable
					data={data(displayedStudents)}
					footerLeft={footerButtons}
				/>
			)}
			<ProtocolVerificationDialog
				open={verificationDialogOpen}
				onClose={() => setVerificationDialogOpen(false)}
				stageKey='IIIA'
				title='Weryfikacja protokołu (Etap IIIA)'
				students={displayedStudents}
			/>
			<Dialog
				open={assignDialogOpen}
				onClose={() => {
					if (!assigning) {
						setAssignDialogOpen(false);
					}
				}}
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
					Przypisanie komisji
					<IconButton
						size='small'
						onClick={() => setAssignDialogOpen(false)}
						disabled={assigning}
					>
						<CloseIcon fontSize='small' />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Typography
						variant='body2'
						sx={{ mb: 1 }}
					>
						{assignStudent?.firstName} {assignStudent?.lastName}
					</Typography>
					<FormControl
						fullWidth
						size='small'
					>
						<InputLabel id='assign-jury-pair-label'>Komisja</InputLabel>
						<Select
							labelId='assign-jury-pair-label'
							label='Komisja'
							value={assignPairId}
							onChange={(event) => setAssignPairId(event.target.value === '' ? '' : Number(event.target.value))}
						>
							<MenuItem value=''>Brak przypisania</MenuItem>
							{assignStudent &&
								getAllowedPairsForStudent(assignStudent).map((pair) => (
									<MenuItem
										key={pair.id}
										value={pair.id}
									>
										{pair.name}
									</MenuItem>
								))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAssignDialogOpen(false)}
						disabled={assigning}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						onClick={handleAssignSave}
						disabled={assigning}
					>
						Zapisz
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={workStatusDialogOpen}
				onClose={() => {
					if (!workStatusSaving) {
						setWorkStatusDialogOpen(false);
					}
				}}
			>
				<DialogTitle>Status pracy</DialogTitle>
				<DialogContent>
					<Typography
						variant='body2'
						sx={{ mb: 1 }}
					>
						{workStatusStudent?.firstName} {workStatusStudent?.lastName}
					</Typography>
					<FormControl
						fullWidth
						size='small'
					>
						<InputLabel id='work-status-label'>Status pracy</InputLabel>
						<Select
							labelId='work-status-label'
							label='Status pracy'
							value={workStatusValue}
							onChange={(event) => setWorkStatusValue(String(event.target.value || ''))}
						>
							<MenuItem value=''>-BRAK-</MenuItem>
							{WORK_STATUS_OPTIONS.map((status) => (
								<MenuItem
									key={status}
									value={status}
								>
									{status}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setWorkStatusDialogOpen(false)}
						disabled={workStatusSaving}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						onClick={handleWorkStatusSave}
						disabled={workStatusSaving}
					>
						Zapisz
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ThirdAStageTable;
